import React, { Fragment, useEffect } from "react";
import { Link } from "react-router-dom";

import Header from "../../../components/Header/Header";
import PageTitle from "../../../components/pagetitle/PageTitle";
import Footer from "../../../components/footer/Footer";
import Scrollbar from "../../../components/scrollbar/scrollbar";

import "./SiliconValidation.css";
import "./PageTheme.css";
import serviceImage from "../../../images/about/testing.jpg";

type Block = { title?: string; text?: string; lines?: string[]; raw: string };

const config = {
  "title": "Silicon Validation",
  "category": "Validation, Automation & Product Engineering",
  "visual": "validation",
  "folder": "SiliconValidation",
  "sections": [
    [
      "Silicon Validation",
      "Validating Silicon for Reliable Product Performance\nSuccessful silicon must do more than meet design intent—it must perform reliably in real-world operating conditions. Silicon Validation confirms functionality, verifies interfaces, measures performance, and identifies potential issues before products move toward deployment. It provides the engineering confidence needed to reduce development risk and ensure dependable product performance.\n\nDigicomm Semiconductor delivers specialized Silicon Validation services that support the complete post-silicon validation lifecycle. From hardware bring-up and functional validation to performance characterization, interface validation, debug, and system-level verification, our engineering teams work closely with customers to validate silicon thoroughly through disciplined execution and structured engineering methodologies.\n\nPrimary CTA\n\nDiscuss Your Silicon Validation Requirements"
    ],
    [
      "Silicon Validation",
      "Silicon Validation\nSilicon Validation is the critical engineering phase where design intent is verified against real hardware. It confirms that silicon performs reliably under expected operating conditions by validating functionality, interfaces, performance, power behavior, and system interactions. This process helps identify issues early, improve engineering confidence, and reduce the risk of downstream product failures.\n\nDigicomm Semiconductor provides comprehensive Silicon Validation services that support the complete post-silicon validation lifecycle. Our engineering teams work collaboratively with customers to perform hardware bring-up, functional validation, interface verification, performance characterization, debug, and system-level validation—ensuring silicon is thoroughly evaluated before product deployment.\n\nOur Silicon Validation Capabilities\nHardware Bring-up\nFunctional Validation\nInterface & Protocol Validation\nPerformance Characterization\nPower & Thermal Validation\nDebug & Root Cause Analysis\nSystem-Level Validation\nReliability & Production Readiness Support"
    ],
    [
      "Why Silicon Validation Matters",
      "Why Silicon Validation Matters\nFabricated silicon marks an important milestone, but it is only the beginning of proving a product's real-world performance. Silicon Validation confirms that devices operate as intended across functional, electrical, thermal, and system-level conditions. It helps uncover issues that simulation alone cannot reveal, enabling engineering teams to resolve problems before products reach manufacturing or deployment.\n\nA disciplined validation process minimizes development risk, accelerates debugging, improves product quality, and provides the confidence required to move from prototype to production. As semiconductor devices become increasingly complex, comprehensive Silicon Validation plays a critical role in ensuring dependable product performance and long-term reliability.\n\nKey Outcomes\nConfident Functional Verification\n\nVerify that silicon performs according to design intent under real operating conditions.\n\nEarly Issue Detection\n\nIdentify functional, interface, or performance issues before they impact production schedules or product reliability.\n\nImproved Product Quality\n\nValidate system behavior across operating scenarios to support robust and reliable product performance.\n\nAccelerated Debug & Root Cause Analysis\n\nReduce engineering effort through structured validation, systematic debugging, and efficient issue resolution.\n\nReduced Development Risk\n\nResolve post-silicon challenges early, minimizing costly redesigns and shortening the path to production.\n\nProduction Readiness\n\nEstablish engineering confidence that silicon is validated, optimized, and ready for successful product integration and deployment."
    ],
    [
      "Our Silicon Validation Capabilities",
      "Our Silicon Validation Capabilities\nEffective Silicon Validation requires a structured engineering approach that verifies functionality, characterizes performance, resolves issues, and confirms system behavior under real operating conditions. Digicomm provides specialized validation services that help customers accelerate product readiness while improving engineering confidence and reducing development risk.\n\nHardware Bring-up\n\nEstablish stable hardware operation, configure development platforms, and enable initial silicon evaluation for subsequent validation activities.\n\nFunctional Validation\n\nVerify that silicon operates according to design intent by validating functional behavior across supported features and operating scenarios.\n\nInterface & Protocol Validation\n\nValidate communication interfaces and protocol compliance to ensure reliable interoperability with connected devices and system components.\n\nPerformance Characterization\n\nMeasure performance, latency, throughput, and operating behavior across different workloads and environmental conditions.\n\nPower & Thermal Validation\n\nEvaluate power consumption, thermal behavior, and operating efficiency to support reliable performance within specified limits.\n\nDebug & Root Cause Analysis\n\nInvestigate functional anomalies through systematic debugging and root cause analysis to accelerate issue resolution.\n\nSystem-Level Validation\n\nValidate silicon within the complete hardware and software environment to confirm stable system operation and expected application behavior.\n\nReliability & Production Readiness\n\nSupport long-duration validation, stress testing, and final engineering assessments to ensure silicon is ready for product integration and production."
    ],
    [
      "Why Digicomm for Silicon Validation",
      "Why Digicomm for Silicon Validation\nComprehensive Silicon Validation demands technical expertise, disciplined execution, and a structured engineering approach that extends beyond identifying issues to understanding their root causes and enabling reliable product performance. Digicomm combines experienced validation engineers, proven methodologies, and collaborative execution to help customers validate silicon efficiently while maintaining quality, schedule, and engineering confidence throughout the product development lifecycle.\n\nWhat Sets Us Apart\nComprehensive Validation Expertise\n\nEngineering teams experienced in functional validation, hardware bring-up, interface validation, performance characterization, and system-level validation across diverse semiconductor applications.\n\nStructured Validation Methodologies\n\nDisciplined validation processes that improve test coverage, accelerate issue identification, and ensure consistent engineering execution.\n\nEfficient Debug & Root Cause Analysis\n\nSystematic debugging techniques that help isolate issues quickly, reduce investigation time, and support faster engineering resolution.\n\nCollaborative Engineering Engagement\n\nSeamless integration with customer engineering teams, enabling effective coordination across design, verification, software, and product engineering functions.\n\nFocus on Product Readiness\n\nValidation activities aligned with the objective of delivering reliable, production-ready silicon through rigorous engineering and dependable execution."
    ],
    [
      "Technology Expertise",
      "Technology Expertise\nEffective Silicon Validation requires expertise across hardware platforms, validation environments, communication interfaces, debugging tools, and system-level test frameworks. Digicomm's engineering teams work across a broad range of technologies to verify silicon functionality, characterize performance, diagnose issues, and establish confidence in real-world product operation.\n\nValidation Platforms\nEvaluation Boards\nFPGA-Based Validation Platforms\nDevelopment & Reference Boards\nCustom Validation Setups\nPrototype Systems\nInterface & Protocol Validation\nPCI Express (PCIe)\nUSB\nEthernet\nMIPI\nI²C\nSPI\nUART\nCAN\nJTAG\nGPIO\nDebug & Analysis\nHardware Debugging\nLogic Analysis\nSignal Integrity Analysis\nProtocol Analysis\nRoot Cause Investigation\nFailure Analysis\nTest & Measurement\nOscilloscopes\nLogic Analyzers\nProtocol Analyzers\nPower Measurement Instruments\nEnvironmental Test Equipment\nAutomated Test Setups\nEngineering Environments\nLinux-Based Validation Environments\nEmbedded Software Integration\nTest Automation Frameworks\nPython-Based Validation Scripts\nCI/CD Validation Workflows\nIssue Tracking & Collaboration Platforms"
    ],
    [
      "Validate Silicon with Confidence",
      "Validate Silicon with Confidence\nSuccessful silicon is measured not only by successful fabrication, but by consistent performance in real-world operating conditions. From hardware bring-up and functional validation to system-level verification and production readiness, Digicomm Semiconductor provides the engineering expertise to help you validate silicon thoroughly, resolve issues efficiently, and move confidently toward product deployment.\n\nPrimary CTA\n\nDiscuss Your Silicon Validation Requirements"
    ]
  ]
} as const;

const splitBlocks = (raw: string): string[] =>
  raw.split(/\n\s*\n/g).map((x) => x.trim()).filter(Boolean);

const asBlock = (raw: string): Block => {
  const lines = raw.split("\n").map((x) => x.trim()).filter(Boolean);
  if (lines.length >= 2 && lines[0].length <= 74) {
    const rest = lines.slice(1);
    const listLike = rest.length >= 3 && rest.every((x) => x.length < 78);
    if (listLike) return { title: lines[0], lines: rest, raw };
    return { title: lines[0], text: rest.join(" "), raw };
  }
  if (lines.length === 1) return { text: lines[0], raw };
  return { text: lines.join(" "), raw };
};

const getHero = (section: readonly string[], pageTitle: string) => {
  const merged = section.join("\n\n");
  const blocks = splitBlocks(merged);
  const filtered = blocks.filter((x) => x !== "Primary CTA");
  if (filtered[0] === pageTitle) filtered.shift();

  const first = filtered.shift() || pageTitle;
  const firstLines = first.split("\n").map((x) => x.trim()).filter(Boolean);
  const headline = firstLines[0] || pageTitle;
  const body: string[] = [];

  if (firstLines.length > 1) body.push(firstLines.slice(1).join(" "));
  filtered.forEach((b) => body.push(b));

  let cta = "";
  if (body.length && body[body.length - 1].length < 85) cta = body.pop() || "";
  return { headline, body, cta };
};

const Arrow = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h13M13 7l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const EngineeringVisual: React.FC = () => (
  <div className="ses-visual service-engineering-image">
    <img src={serviceImage} alt="Semiconductor testing and validation equipment" />
  </div>
);

const ContentPanel: React.FC<{ raw: string; index: number }> = ({ raw, index }) => {
  const blocks = splitBlocks(raw).filter((x) => x !== "Primary CTA");
  return (
    <div className="ses-block-grid">
      {blocks.map((rawBlock, i) => {
        const block = asBlock(rawBlock);
        const long = rawBlock.length > 430;
        const labelOnly =
          !block.title &&
          !!block.text &&
          block.text.length < 80 &&
          (/[:：]$/.test(block.text) || i === 0);

        return (
          <article
            className={`ses-block ${long ? "ses-block--wide" : ""} ${labelOnly ? "ses-block--label" : ""}`}
            key={`${index}-${i}`}
          >
            {block.title && <h3>{block.title}</h3>}
            {block.text && (labelOnly ? <h3>{block.text}</h3> : <p>{block.text}</p>)}
            {block.lines && <ul>{block.lines.map((line) => <li key={line}>{line}</li>)}</ul>}
          </article>
        );
      })}
    </div>
  );
};

const RelatedPanel: React.FC<{ raw: string }> = ({ raw }) => {
  const blocks = splitBlocks(raw);
  const routeMap: Record<string, string> = {
    "Analog & Mixed Signal Design & Layout": "/services/analog-mixed-signal",
    "Analog & Mixed Signal": "/services/analog-mixed-signal",
    "Static Timing Analysis (STA)": "/services/static-timing-analysis",
    "FPGA Engineering": "/services/fpga-engineering",
    "Embedded Software Systems": "/services/embedded-software-systems",
    "Hardware Platform Engineering": "/services/hardware-platform-engineering",
    "Networking Solutions": "/services/networking-solutions",
    "Silicon Validation": "/services/silicon-validation",
    "EDA & Design Automation": "/services/eda-design-automation",
    "Product Engineering": "/services/product-engineering",
    "ASIC & SoC Design": "/services/asic-soc-design",
    "Physical Design": "/services/physical-design",
    "Verification Services": "/services/verification",
  };

  const cards: React.ReactNode[] = [];
  let group = "";

  blocks.forEach((b, i) => {
    if (/^Related .*:$/.test(b.trim())) {
      group = b.trim();
      return;
    }
    if (b === "Learn More →") return;

    const lines = b.split("\n").map((x) => x.trim()).filter(Boolean);
    const title = lines[0];
    const desc = lines.slice(1).join(" ");

    if (routeMap[title]) {
      cards.push(
        <Link className="ses-related-card" to={routeMap[title]} key={`${title}-${i}`}>
          <span>{group}</span>
          <h3>{title}</h3>
          {desc && <p>{desc}</p>}
          <em>Learn More <Arrow/></em>
        </Link>
      );
    } else {
      cards.push(
        <article className="ses-related-card" key={`${title}-${i}`}>
          <span>{group}</span>
          <h3>{title}</h3>
          {desc && <p>{desc}</p>}
        </article>
      );
    }
  });

  return <div className="ses-related-grid">{cards}</div>;
};

const Page: React.FC = () => {
  const sections = config.sections as readonly (readonly string[])[];
  const hero = getHero(sections[0] || [], config.title);

  const lastSection = sections[sections.length - 1] || [];
  const lastRaw = lastSection.join("\n\n");
  const lastBlocks = splitBlocks(lastRaw).filter((x) => x !== "Primary CTA");
  const ctaLabel =
    lastBlocks.length && lastBlocks[lastBlocks.length - 1].length < 90
      ? lastBlocks[lastBlocks.length - 1]
      : "";
  const ctaContent = ctaLabel ? lastBlocks.slice(0, -1) : lastBlocks;
  const hasRelated = sections.length === 9;

  useEffect(() => {
    document.title = `${config.title} | Digicomm Semiconductor`;
  }, []);

  return (
    <Fragment>
      <div className={`specialized-service-page inner-page validation-redesign`}>
        <div className="body_wrap o-clip">
          <Header />

          <main>
            <PageTitle pageTitle={config.title} pagesub={config.title} />

            <section className="ses-hero">
              <div className="ses-shell ses-two-col">
                <div className="ses-hero__copy">
                  <h1>{hero.headline}</h1>
                  {hero.body.map((p, i) => <p key={i}>{p}</p>)}
                  {hero.cta && (
                    <Link className="ses-btn" to="/contact">
                      {hero.cta}<Arrow/>
                    </Link>
                  )}
                </div>
                <EngineeringVisual />
              </div>
            </section>

            {sections.slice(1, hasRelated ? -2 : -1).map((section, idx) => {
              const raw = section.join("\n\n");
              const blocks = splitBlocks(raw);
              const lead = blocks[0] || "";
              const firstLine = lead.split("\n")[0] || "";

              return (
                <section className={`ses-section ses-section--${idx + 2} ${idx % 2 ? "ses-section--alt" : ""}`} key={idx}>
                  <div className="ses-shell">
                    <div className="ses-section-head">
                      <span>{String(idx + 2).padStart(2, "0")}</span>
                      <h2>{firstLine}</h2>
                    </div>
                    <ContentPanel raw={raw} index={idx}/>
                  </div>
                </section>
              );
            })}

            {hasRelated && (
              <section className="ses-section ses-related">
                <div className="ses-shell">
                  <RelatedPanel raw={sections[sections.length - 2].join("\n\n")}/>
                </div>
              </section>
            )}

            <section className="ses-cta">
              <div className="ses-shell ses-two-col">
                <div className="ses-cta__copy">
                  {ctaContent.map((b, i) => {
                    const lines = b.split("\n").map((x) => x.trim()).filter(Boolean);
                    return i === 0
                      ? <h2 key={i}>{lines.join(" ")}</h2>
                      : <p key={i}>{lines.join(" ")}</p>;
                  })}

                  {ctaLabel && (
                    <Link className="ses-btn" to="/contact">
                      {ctaLabel}<Arrow/>
                    </Link>
                  )}
                </div>
                <EngineeringVisual />
              </div>
            </section>
          </main>

          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default Page;

